import {
  httpGet,
  httpPost,
  httpPut,
  httpPatch,
  httpGetWithOutToken,
  httpPostWithOutToken,
  httpDelete,
} from "../../utils/httpMethods";
import { USER_CREATE_MANUAL, USER_URL } from "../restEndpoint";
import * as Sentry from "@sentry/react";
import { normalizeEmail } from "../../utils/emailNormalizer";

const { logger } = Sentry;

export const employeeUpdate = async (id, planName) => {
  return await httpPost({
    url: `${USER_URL}/upgrade-plan`,
    payload: {
      userId: id,
      planName,
    },
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Employee update error", { error: err.message });
      return err?.response;
    });
};

export const getAllIndividualUsers = async () => {
  return await httpGet({ url: `${USER_URL}/individual` })
    .then((res) => {
      logger.debug("Get all users successful");
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Get all users error", { error: err.message });
      return err?.response;
    });
};

export const updateUserStatus = async (id, status) => {
  return await httpPut({
    url: `${USER_URL}/${id}`,
    payload: { status },
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Update user status error", { id, error: err.message });
      return err?.response;
    });
};

export const resetPassword = async (id, newPassword) => {
  return await httpPost({
    url: `${USER_URL}/reset-password`,
    payload: { userId: id, newPassword },
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Reset password error", { id, error: err.message });
      return err?.response;
    });
};

// Update user
export const updateUser = async (id, payload) => {
  return await httpPut({ url: `${USER_URL}/${id}`, payload })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Update user error", { id, error: err.message });
      return err?.response;
    });
};

// Assign admin role to a user
export const assignAdminRole = async (userId, roleId) => {
  return await httpPost({
    url: `${USER_URL}/assign-admin-role`,
    payload: { userId, roleId: roleId || null }, // Allow null to remove role
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Assign admin role error", { userId, roleId, error: err.message });
      return err?.response;
    });
};

// Create admin user
export const createAdminUser = async (userData) => {
  return await httpPost({
    url: `${USER_URL}/create-admin-user`,
    payload: userData,
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Create admin user error", { error: err.message });
      return err?.response;
    });
};

// Get all admin users
export const getAllAdminUsers = async () => {
  return await httpGet({ url: `${USER_URL}/admin-users` })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Get admin users error", { error: err.message });
      return err?.response;
    });
};

// Delete admin user
export const deleteAdminUser = async (userId) => {
  return await httpDelete({ url: `${USER_URL}/admin-users/${userId}` })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Delete admin user error", { userId, error: err.message });
      return err?.response;
    });
};

// Request a plan upgrade
export const requestPlanUpgrade = async ({
  userId,
  planName,
  requestMessage,
}) => {
  return await httpPost({
    url: `${USER_URL}/request-plan-upgrade`,
    payload: { userId, planName, requestMessage },
  })
    .then((res) => res)
    .catch((err) => err?.response);
};

// Get all plan upgrade requests (admin)
export const getAllPlanUpgradeRequests = async () => {
  return await httpGet({
    url: `${USER_URL}/plan-upgrade-requests`,
  })
    .then((res) => {
      // If backend returns { requests: [...] }, return just the array
      if (res?.data?.requests) return { data: res.data.requests };
      return res;
    })
    .catch((err) => err?.response);
};

// Approve a plan upgrade request (admin)
export const approvePlanUpgradeRequest = async (requestId, adminResponse) => {
  return await httpPatch({
    url: `${USER_URL}/approve-plan-upgrade/${requestId}`,
    payload: { adminResponse },
  })
    .then((res) => res)
    .catch((err) => err?.response);
};

// Reject a plan upgrade request (admin)
export const rejectPlanUpgradeRequest = async (requestId, adminResponse) => {
  return await httpPatch({
    url: `${USER_URL}/reject-plan-upgrade/${requestId}`,
    payload: { adminResponse },
  })
    .then((res) => res)
    .catch((err) => err?.response);
};

// Get all individual users with pagination and filters
export const getAllIndividualUsersPaginated = async ({
  page = 1,
  limit = 10,
  status,
  planName,
  search,
} = {}) => {
  const params = new URLSearchParams();
  if (page) params.append("page", page);
  if (limit) params.append("limit", limit);
  if (status !== undefined) params.append("status", status);
  if (planName) params.append("planName", planName);
  if (search) params.append("search", search);
  return await httpGet({ url: `${USER_URL}/individual?${params.toString()}` })
    .then((res) => res)
    .catch((err) => err?.response);
};

// Get user usage and pricing information
export const getUserUsageAndPricing = async (userId) => {
  return await httpGet({ url: `${USER_URL}/usage-pricing/${userId}` })
    .then((res) => {
      logger.debug("Get user usage and pricing successful", { userId });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Get user usage and pricing error", { userId, error: err.message });
      return err?.response;
    });
};

// Get user's first-month billing status (firstMonth flag, credit, base_cost)
export const getUserFirstMonthStatus = async (userId) => {
  return await httpGet({ url: `${USER_URL}/first-month-status/${userId}` })
    .then((res) => res)
    .catch((err) => err?.response);
};

// Get Stripe invoices for a user
export const getStripeInvoices = async (userId) => {
  return await httpGet({ url: `${USER_URL}/stripe-invoices/${userId}` })
    .then((res) => {
      logger.debug("Get Stripe invoices successful", { userId });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Get Stripe invoices error", { userId, error: err.message });
      return err?.response;
    });
};

// Activate trial - Backend creates Stripe Checkout Session
// No card data needed - Stripe handles everything on their hosted page
export const activateTrial = async () => {
  return await httpPost({
    url: `${USER_URL}/activate-trial`,
    payload: {}, // Empty payload - backend creates checkout session
  })
    .then((res) => res)
    .catch((err) => err?.response);
};

// Get current user's trial details
// Calls backend /trial-details to retrieve status and remaining days
export const getTrialDetails = async () => {
  return await httpGet({ url: `${USER_URL}/trial-details` })
    .then((res) => res)
    .catch((err) => err?.response);
};

// Verify trial after Stripe Checkout completion
// Accepts stripe_id (checkout session ID) from URL parameter
export const verifyTrial = async (stripeId) => {
  return await httpGet({
    url: `${USER_URL}/verify-trial`,
    params: { stripe_id: stripeId },
  })
    .then((res) => res)
    .catch((err) => err?.response);
};

// Send verification email (sends 6-digit code)
// Can be called with email (unauthenticated) or without (authenticated user)
export const sendVerificationEmail = async (email = null) => {
  // Normalize email before sending
  const payload = email ? { email: normalizeEmail(email) } : {};
  const url = `${USER_URL}/send-verification-email`;
  
  logger.info("Sending verification email API call", { email, hasEmail: !!email });

  try {
    // If email is provided, use unauthenticated request, otherwise use authenticated
    const httpMethod = email ? httpPostWithOutToken : httpPost;
    const response = await httpMethod({
      url,
      payload,
    });
    logger.info("Send verification email response", { status: response?.status });
    return response;
  } catch (err) {
    Sentry.captureException(err);
    logger.error("Send verification email error", { error: err.message });
    return err?.response || { status: err?.response?.status || 400, data: err?.response?.data || { message: "Failed to send verification email" } };
  }
};

// Verify email with 6-digit code (unauthenticated - user hasn't logged in yet)
export const verifyEmail = async (code, email) => {
  const url = `${USER_URL}/verify-email`;
  // Normalize email before sending
  // Ensure code is sent as a string (not a number)
  const payload = {
    code: String(code).trim(),
    email: normalizeEmail(String(email).trim()),
  };
  
  logger.info("Verifying email with code API call", { hasCode: !!code, hasEmail: !!email });

  try {
    const response = await httpPostWithOutToken({
      url,
      payload,
    });
    logger.info("Verify email response", { status: response?.status, success: response?.data?.success });
    return response;
  } catch (err) {
    Sentry.captureException(err);
    logger.error("Verify email error", { error: err.message });
    return err?.response || { status: err?.response?.status || 400, data: err?.response?.data || { message: "Failed to verify email" } };
  }
};

// Delete current logged-in user's account
// Backend will:
// - Check Stripe for any outstanding balance (open invoices)
// - Block deletion if there is unpaid balance
// - Cancel Stripe subscriptions and delete user if everything is clear
export const deleteCurrentUserAccount = async () => {
  const url = `${USER_URL}/delete-account`;
  logger.info("Delete account API call");

  try {
    const response = await httpDelete({ url });
    logger.info("Delete account response", { status: response?.status });
    return response;
  } catch (err) {
    Sentry.captureException(err);
    logger.error("Delete account error", { error: err.message });
    // Surface backend's structured error (e.g. outstandingBalance) to caller
    return (
      err?.response || {
        status: 500,
        data: { message: "Failed to delete account" },
      }
    );
  }
};

// Send OTP for login
export const sendLoginOTP = async (email) => {
  const url = `${USER_URL}/send-login-otp`;
  const payload = { email: String(email).trim() };
  
  console.log("📧 Sending login OTP API call:", {
    url,
    email: payload.email,
  });

  try {
    const response = await httpPostWithOutToken({
      url,
      payload,
    });
    console.log("✅ Send login OTP response:", response);
    return response;
  } catch (err) {
    console.error("❌ Send login OTP error:", err);
    return err?.response || {
      status: 400,
      data: { success: false, message: "Failed to send login code" },
    };
  }
};

// Verify current user (refresh user data from server)
export const verifyUser = async () => {
  return await httpGet({ url: `${USER_URL}/verify` })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Verify user error", { error: err.message });
      return err?.response;
    });
};

// Verify OTP and login
export const verifyLoginOTP = async (email, code) => {
  const url = `${USER_URL}/verify-login-otp`;
  const payload = {
    email: String(email).trim(),
    code: String(code).trim().replace(/\s/g, ''),
  };
  
  console.log("🔐 Verifying login OTP API call:", {
    url,
    email: payload.email,
    codeLength: payload.code.length,
  });

  try {
    const response = await httpPostWithOutToken({
      url,
      payload,
    });
    console.log("✅ Verify login OTP response:", response);
    return response;
  } catch (err) {
    console.error("❌ Verify login OTP error:", err);
    return err?.response || {
      status: 400,
      data: { success: false, message: "Failed to verify login code" },
    };
  }
};

// Create manual user (admin only - for sponsored/non-billing accounts)
export const createManualUser = async (userData) => {
  return await httpPost({
    url: `${USER_CREATE_MANUAL}/create-manual`,
    payload: userData,
  })
    .then((res) => {
      logger.debug("Create manual user successful");
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Create manual user error", { error: err.message });
      return err?.response;
    });
};

// Bulk upload users via CSV (admin only)
export const bulkUploadUsers = async (usersData) => {
  return await httpPost({
    url: `${USER_CREATE_MANUAL}/bulk-upload`,
    payload: { users: usersData },
  })
    .then((res) => {
      logger.debug("Bulk upload users successful");
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Bulk upload users error", { error: err.message });
      return err?.response;
    });
};

// Delete single user (admin only)
export const deleteUser = async (userId) => {
  return await httpDelete({
    url: `${USER_CREATE_MANUAL}/${userId}`,
  })
    .then((res) => {
      logger.info("Delete user successful", { userId });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Delete user error", { userId, error: err.message });
      return err?.response;
    });
};

// Bulk delete users (admin only)
export const bulkDeleteUsers = async (userIds) => {
  return await httpPost({
    url: `${USER_CREATE_MANUAL}/bulk-delete`,
    payload: { userIds },
  })
    .then((res) => {
      logger.info("Bulk delete users successful", { count: userIds.length });
      return res;
    })
    .catch((err) => {
      Sentry.captureException(err);
      logger.error("Bulk delete users error", { error: err.message });
      return err?.response;
    });
};
